import React, { Component, lazy, Suspense } from 'react';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';
import { isDirty, initialize } from 'redux-form';
import { FormattedHTMLMessage, injectIntl } from 'react-intl';
import { displayMode } from 'common/forms/displayMode';

import {
  validate,
  getFormById,
  certifyForm,
  setSubmissionStage,
  confirmForm,
  submitForm,
  getPreviewPdf,
  authenticatePin,
  sendVerificationPin,
  hidePinVerificationModal,
  clearDefaultForm
} from 'actions';

import LoadingWrapper from 'components/Common/LoadingWrapper';
import ServerError from 'components/Errors/ServerError';
import NotFoundError from 'components/Errors/NotFoundError';
import CustomerAvatar from 'components/CustomerAvatar';
import Button from 'components/Common/Buttons';
import ScrollToTop from 'components/ScrollToTop';
import ValidationResults from 'components/ValidationResults';
import ValidationResultsStacked from 'components/ValidationResultsStacked';
import FormConfirmation from 'components/Modals/FormConfirmation';
import FormSigning from 'components/Modals/FormSigning';
import PinVerificationModal from 'components/Modals/PinVerificationModal';
import PenaltiesOfPerjuryModal from 'components/Modals/PenaltiesOfPerjuryModal';
import { submissionStages } from 'common/forms/submissionStages';
import SupportingDocumentsModal from 'components/Modals/SupportingDocumentsModal';
import WorkflowStages from 'common/workflows/WorkflowStages';
import pinType from 'common/pinType';

import {
  LazyBlankBene2017,
  LazyBlankBene2021,
  LazyBlankBen,
  LazyBlankBen2021,
  LazyBlankW92018,
  LazyBlankEci,
  LazyBlankEci2021,
  LazyBlankExp2017,
  LazyBlankImy2017,
  LazyBlankCrsE,
  LazyBlankCrsCp,
  LazyBlankCrsI
} from './lazyBlanks';

const FormQuestionnaire = lazy(() => import(/* webpackChunkName: "blank.questionnaires" */ 'components/Questionnaires/FormQuestionnaire'));

class FormContainer extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    match: PropTypes.shape({
      path: PropTypes.string,
      params: PropTypes.shape({
        formType: PropTypes.string,
        formId: PropTypes.string
      })
    }).isRequired,
    history: PropTypes.shape({
      go: PropTypes.func,
      block: PropTypes.func,
      push: PropTypes.func,
      location: PropTypes.shape({
        pathname: PropTypes.string
      })
    }).isRequired,
    isGettingForm: PropTypes.bool,
    formData: PropTypes.shape({
      isOnlineForm: PropTypes.bool,
      originalFileName: PropTypes.string
    }),
    isValidatingForm: PropTypes.bool,
    validationResult: PropTypes.shape({
      result: PropTypes.shape({}),
      rejections: PropTypes.oneOfType([
        PropTypes.shape({
          length: PropTypes.number
        }),
        PropTypes.arrayOf(PropTypes.shape({}))
      ]),
      valid: PropTypes.bool
    }),
    supportingDocs: PropTypes.arrayOf(PropTypes.shape({})),
    workflowState: PropTypes.shape({
      stage: PropTypes.number
    }),
    instructionToggled: PropTypes.string,
    dirty: PropTypes.bool,
    reduxFormName: PropTypes.string,
    formError: PropTypes.shape({
      code: PropTypes.number,
      message: PropTypes.string
    }),
    submissionStage: PropTypes.string,
    pdfData: PropTypes.string,
    isFetchingPdfForm: PropTypes.bool,
    showSupportingDocs: PropTypes.bool,
    previewOnlyMode: PropTypes.bool,
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired
    }).isRequired,
    formDisplayMode: PropTypes.number.isRequired,
    usePreQualifyingQuestionnaire: PropTypes.bool,
    customerName: PropTypes.string,
    authenticationType: PropTypes.number,
    authenticationPinVerified: PropTypes.bool,
    showPinVerification: PropTypes.bool,
    emailAddress: PropTypes.string,
    defaultFormType: PropTypes.string,
  };

  static defaultProps = {
    isValidatingForm: false,
    validationResult: null,
    supportingDocs: null,
    workflowState: null,
    isGettingForm: true,
    formData: null,
    formError: null,
    instructionToggled: null,
    dirty: false,
    reduxFormName: null,
    submissionStage: submissionStages.Validating,
    pdfData: null,
    isFetchingPdfForm: false,
    showSupportingDocs: false,
    previewOnlyMode: false,
    usePreQualifyingQuestionnaire: false,
    customerName: null,
    authenticationType: null,
    authenticationPinVerified: false,
    showPinVerification: false,
    emailAddress: null,
    defaultFormType: null,
  };

  static resizeTimer;

  static maxMobileWidth = 992;

  static windowWidth;

  static isMobileSize() {
    return FormContainer.windowWidth <= FormContainer.maxMobileWidth;
  }

  static updateFormDimensions() {
    FormContainer.windowWidth = window.innerWidth;

    if (FormContainer.isMobileSize()) {
      const contentElement = document.getElementById('form-content');
      if (contentElement != null) {
        document.getElementById('form-content').style.height = 'auto';
      }
      return;
    }
    if (FormContainer.resizeTimer) {
      clearInterval(FormContainer.resizeTimer);
      FormContainer.resizeTimer = null;
    }
    FormContainer.resizeTimer = setInterval(() => {
      FormContainer.adjustFormContainerHeight();
    }, 1000, this);
  }

  static adjustFormContainerHeight() {
    clearInterval(FormContainer.resizeTimer);
    FormContainer.resizeTimer = null;

    let height = 0;

    if (!document.getElementById('form-content')
    || !document.getElementById('form-content').firstChild
    || !document.getElementById('blankForm')) {
      return;
    }
    const pages = document.getElementById('form-content').firstChild.childNodes;

    if (!pages || pages.length === 0) {
      return;
    }

    let top = 0;
    let bottom = 0;
    for (let i = 0; i < pages.length; i += 1) {
      if (pages != null && pages.length > 1 && pages[i].className != null) {
        if (pages[i].className.includes('page')) {
          if (top === 0) {
            top = Math.ceil(pages[i].getBoundingClientRect().top);
          }
          bottom = Math.ceil(pages[i].getBoundingClientRect().bottom);
        }
      }
    }

    if (top === 0 || bottom === 0) {
      return;
    }

    height = bottom - top;

    document.getElementById('form-content').style.height = `${ height }px`;
  }

  constructor(props) {
    super(props);

    const { formDisplayMode } = this.props;

    this.state = {
      showMobileView: window.innerWidth <= FormContainer.maxMobileWidth,
      showScrollToTop: formDisplayMode !== displayMode.Stacked && window.scrollY !== 0,
      pdfDownload: null,
      showResult: false,
    };

    this.unblock = props.history.block((location, action) => {
      const { dirty } = this.props;
      if (dirty) {
        return 'Are you sure you want to leave this page?';
      }
      return null;
    });
    this.showScrollToTopIcon = this.showScrollToTopIcon.bind(this);
    this.adjustFormView = this.adjustFormView.bind(this);
  }

  componentDidMount() {
    const { dispatch, match } = this.props;
    const { params: { formType, formId } } = match;
    dispatch(getFormById(formType, formId));

    this.setMainContainerClass();

    window.addEventListener('scroll', this.showScrollToTopIcon, false);
    window.addEventListener('resize', this.adjustFormView, false);
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(newProps) {
    const {
      dispatch,
      match,
      instructionToggled,
      isFetchingPdfForm,
      isValidatingForm
    } = this.props;

    if (match.path !== newProps.match.path && match.params !== newProps.match.params) {
      const { params: { formType, formId } } = newProps.match;
      dispatch(getFormById(formType, formId));
      return;
    }

    if (isFetchingPdfForm && !newProps.isFetchingPdfForm && newProps.pdfData) {
      this.setState({
        pdfDownload: newProps.pdfData
      });
      const link = document.createElement('a');
      link.href = newProps.pdfData;
      link.setAttribute('target', '_blank');
      link.setAttribute('download', `${ match.params.formType }.pdf`);
      document.body.appendChild(link);
      link.click();
    } else {
      this.setState({
        pdfDownload: null
      });
    }

    if (!newProps.isValidatingForm && isValidatingForm) {
      this.setState({ showResult: true });
    }

    if (newProps.instructionToggled && newProps.instructionToggled !== instructionToggled) {
      FormContainer.updateFormDimensions();
    }
  }

  componentDidUpdate(prevProps) {
    const { dispatch, isValidatingForm, workflowState, history, defaultFormType } = this.props;
    FormContainer.updateFormDimensions();

    if (!isValidatingForm && prevProps.isValidatingForm) {
      const element = document.getElementById('validation-results');
      if (element != null) {
        element.scrollIntoView();
      }
    }

    if (workflowState && workflowState.stage > 2) {
      if (defaultFormType) {
        dispatch(clearDefaultForm());
      }
      history.push(`${ history.location.pathname }/success`);
    }
  }

  componentWillUnmount() {
    const { pdfDownload } = this.state;
    window.removeEventListener('resize', this.adjustFormView, false);
    window.removeEventListener('scroll', this.showScrollToTopIcon, false);

    this.unblock();

    const mainContainer = document.getElementById('main-container');
    if (mainContainer) {
      mainContainer.className = 'container main-content';
    }

    if (pdfDownload) {
      URL.revokeObjectURL(pdfDownload);
    }
  }

  onValidateForm(formType, values) {
    const { dispatch, match, reduxFormName } = this.props;
    const { params: { formId } } = match;

    dispatch(validate(formType, formId, values));
    dispatch(initialize(reduxFormName, values));
  }

  onFormConfirmationConfirm() {
    const { dispatch, match } = this.props;
    const { params: { formId, formType } } = match;
    dispatch(confirmForm(formType, formId));
  }

  onFormConfirmationCancel() {
    this.resetSubmisson();
  }

  onCertificationConfirm() {
    const { dispatch, match: { params: { formId } } } = this.props;
    dispatch(certifyForm(formId));
  }

  onCertificationCancel() {
    this.resetSubmisson();
  }

  onFormSigningCancel() {
    this.resetSubmisson();
  }

  onUploadDocumentClick() {
    this.setState({ showSupportingDocs: true });
  }

  onPinVerificationCancel() {
    const { dispatch } = this.props;
    dispatch(hidePinVerificationModal());
    this.resetSubmisson();
  }

  onPinVerificationConfirm(pin, pinVerificationType) {
    const { dispatch } = this.props;
    dispatch(authenticatePin(pin, pinVerificationType));
  }

  getFormValidState() {
    const { validationResult } = this.props;

    if (!validationResult) {
      return 'validation-result-icon none';
    }
    if (validationResult != null && validationResult.valid != null && validationResult.valid !== 'undefined') {
      return `validation-result-icon ${ ((validationResult.valid) ? 'valid' : 'invalid') }`;
    }
    return '';
  }

  getBlankComponent(BlankComponent) {
    const { match: { params: { formType } } } = this.props;

    return (
      <React.Suspense fallback={ <div className="loading-container"><LoadingWrapper text="Please Wait..." /></div> }>
        <BlankComponent
          onSubmit={ values => this.onValidateForm(formType, values) }
          onValidationResultsClick={ () => this.setState({ showResult: true }) }
          onSupportingDocumentsClick={ () => this.setState({ showSupportingDocs: true }) }
        />
      </React.Suspense>
    );
  }

  getWrapperClasses() {
    const { match: { params: { formType } }, formDisplayMode } = this.props;
    const { showMobileView } = this.state;
    let classes = `${ showMobileView ? 'mobile-' : '' }blank-wrapper`;

    classes += formType && formType.indexOf('w') === 0 && formDisplayMode !== displayMode.Stacked ? ' w-blank-wrapper' : '';
    classes += formType && (formType.indexOf('crs') === 0 || formType.indexOf('ucrs') === 0) && formDisplayMode !== displayMode.Stacked ? ' crs-blank-wrapper' : '';
    classes += formType && formType.indexOf('rc') === 0 && formDisplayMode !== displayMode.Stacked ? ' rc-blank-wrapper' : '';
    classes += (navigator.userAgent.indexOf('Edge') < 0 && navigator.userAgent.indexOf('Edg') !== -1 && window.screenTop > 0) ? ' wrapper_fullscreen' : '';
    return classes;
  }

  getActionElements() {
    const { showMobileView, showScrollToTop } = this.state;
    const {
      match: { params: { formType } },
      isGettingForm,
      isValidatingForm,
      formError,
      workflowState,
      dirty,
      formDisplayMode,
    } = this.props;
    const showStackedView = formDisplayMode === displayMode.Stacked && !showMobileView;
    return (
      <React.Fragment>
        { !isGettingForm && formError == null && workflowState != null && <div className="form-action-bar">
          { showStackedView && <p><FormattedHTMLMessage id="stackedFormView.instructionText" /></p>}
          { this.showSignAndSubmit() && !isGettingForm && !isValidatingForm && <Button
            id="sign-submit-action-button"
            value={ this.signSubmitButtonText() }
            type="button"
            className={ `btn btn-primary btn-inline${ isValidatingForm ? ' btn-active' : '' } mt-1${ showStackedView ? ' btn-small' : '' }` }
            onClick={ () => this.signAndSubmit() }
            disabled={ workflowState.stage > 2 }
          /> }
          { <Button
            id="check-button"
            value="Check"
            type="button"
            className={ `btn btn-primary btn-inline${ isValidatingForm ? ' btn-active' : '' } mt-1${ showStackedView ? ' btn-small' : '' }` }
            disabled={ [].includes(formType) || isGettingForm || isValidatingForm || (workflowState.stage >= 2 && !dirty) }
            onClick={ () => document.getElementById('dummy-validate-form-btn').click() }
          /> }
          { (formDisplayMode !== displayMode.Stacked || showMobileView) && showScrollToTop && <ScrollToTop
            elementId={ showMobileView ? null : 'form-wrapper' }
          /> }
          { showStackedView && <Button value="Upload Document" className={ `btn btn-secondary btn-inline mb-2"${ showStackedView ? ' btn-small' : '' }` } onClick={ () => this.onUploadDocumentClick() } /> }
        </div> }
      </React.Fragment>
    );
  }

  setMainContainerClass() {
    const { formDisplayMode } = this.props;
    const { showMobileView } = this.state;

    const mainContainer = document.getElementById('main-container');
    const stackedElement = document.getElementById('stacked-view');
    if (formDisplayMode === displayMode.Stacked && stackedElement && mainContainer && !showMobileView) {
      mainContainer.className = 'container main-content stacked';
    } else {
      mainContainer.className = 'container main-content';
    }
  }

  isValidated() {
    const { validationResult } = this.props;
    return (validationResult != null);
  }

  isValidForm() {
    const { validationResult } = this.props;
    return (validationResult && validationResult.valid);
  }

  isDraft() {
    const { workflowState } = this.props;

    if (!workflowState) {
      return false;
    }

    // WF Stage 1 or 2 - Draft or Pending Signature
    return (workflowState.stage === WorkflowStages.Draft || workflowState.stage === WorkflowStages.PendingSignature);
  }

  isOnlineForm() {
    const { formData } = this.props;
    return (formData != null && (formData.isOnlineForm || !formData.originalFileName));
  }

  adjustFormView() {
    this.setState({ showMobileView: window.innerWidth <= FormContainer.maxMobileWidth });
    FormContainer.updateFormDimensions();
    this.setMainContainerClass();
  }

  isNoFormSupported() {
    const { match: { params: { formType } } } = this.props;

    switch (formType) {
      case 'w9_2018':
        return true;
      case 'w8ben':
      case 'w8ben_2021':
        return true;
      case 'crsi':
        return true;
      case 'crse':
        return true;
      case 'crscp':
        return true;
      default:
        return false;
    }
  }

  showScrollToTopIcon(e, elementId) {
    const { showScrollToTop } = this.state;
    if (elementId) {
      const element = document.getElementById(elementId);
      if (element) {
        const scrolled = element.scrollTop !== 0;
        if (showScrollToTop !== scrolled) {
          this.setState({ showScrollToTop: element.scrollTop !== 0 });
        }
      }
    } else {
      const scrolled = window.scrollY !== 0;
      if (showScrollToTop !== scrolled) {
        this.setState({ showScrollToTop: window.scrollY !== 0 });
      }
    }
  }

  signAndSubmit() {
    const { dispatch, match, workflowState, previewOnlyMode, authenticationType, authenticationPinVerified } = this.props;
    const { params: { formId, formType } } = match;

    if (!previewOnlyMode && authenticationType && authenticationType === 1 && !authenticationPinVerified && !formType.startsWith('crs')) {
      dispatch(sendVerificationPin(pinType.ElectornicSignaturePin));
    }

    if (workflowState && workflowState.stage === 2) {
      dispatch(setSubmissionStage(submissionStages.Signing));
      return;
    }

    dispatch(submitForm(formId, submissionStages.Confirmation));
  }

  resetSubmisson() {
    const { dispatch } = this.props;
    dispatch(setSubmissionStage(submissionStages.Validating));
  }

  signSubmitButtonText() {
    const { intl, previewOnlyMode } = this.props;
    const messageId = previewOnlyMode ? 'signsubmit.previewOnlyMode.buttonText' : 'signsubmit.buttonText';
    return intl.formatMessage({ id: messageId });
  }

  showSignAndSubmit() {
    const { validationResult, dirty, usePreQualifyingQuestionnaire } = this.props;
    if (validationResult != null) {
      if (validationResult.rejections == null || validationResult.rejections.length === 0) {
        if (usePreQualifyingQuestionnaire && this.isNoFormSupported()) {
          return true;
        }
        return !dirty;
      }
    }
    return false;
  }

  downloadPreviewPdf() {
    const { dispatch, match } = this.props;
    const { params: { formId } } = match;

    dispatch(getPreviewPdf(formId));
  }

  renderForm() {
    const { match: { params: { formType } } } = this.props;

    switch (formType) {
      case 'w8bene_2017':
        return this.getBlankComponent(LazyBlankBene2017);
      case 'w8bene_2021':
        return this.getBlankComponent(LazyBlankBene2021);
      case 'w8ben':
        return this.getBlankComponent(LazyBlankBen);
      case 'w8ben_2021':
        return this.getBlankComponent(LazyBlankBen2021);
      case 'w9_2018':
        return this.getBlankComponent(LazyBlankW92018);
      case 'w8eci':
        return this.getBlankComponent(LazyBlankEci);
      case 'w8eci_2021':
        return this.getBlankComponent(LazyBlankEci2021);
      case 'w8exp':
        return this.getBlankComponent(LazyBlankExp2017);
      case 'w8imy_2017':
        return this.getBlankComponent(LazyBlankImy2017);
      case 'crse':
        return this.getBlankComponent(LazyBlankCrsE);
      case 'crsi':
        return this.getBlankComponent(LazyBlankCrsI);
      case 'crscp':
        return this.getBlankComponent(LazyBlankCrsCp);
      default:
        return <div>Incorrect blank type</div>;
    }
  }

  render() {
    const { showMobileView, showResult, showSupportingDocs } = this.state;
    const {
      intl,
      match: { params: { formType, formId } },
      isGettingForm,
      workflowState,
      validationResult,
      formError,
      dirty,
      previewOnlyMode,
      formDisplayMode,
      usePreQualifyingQuestionnaire,
      supportingDocs,
      submissionStage,
      isFetchingPdfForm,
      showPinVerification
    } = this.props;

    const showStackedView = formDisplayMode === displayMode.Stacked && !showMobileView;
    const formQuestionnaireSupported = usePreQualifyingQuestionnaire && this.isNoFormSupported();

    return (
      <React.Fragment>
        { (formError != null && formError.code === 404) && <NotFoundError /> }
        { (formError != null && formError.code !== 404) && <ServerError { ...formError } /> }
        { !isGettingForm && formError == null && formType != null && <React.Fragment>
          { formQuestionnaireSupported && <Suspense fallback={ <div className="loading-container"><LoadingWrapper text="Please Wait..." /></div> }>
            <FormQuestionnaire formId={ formId } formType={ formType } onSignAndSubmit={ () => this.signAndSubmit() } showSupportingDocs={ () => this.onUploadDocumentClick() } /></Suspense>}
          { !formQuestionnaireSupported && <div id={ `${ showStackedView ? 'stacked-view' : 'irs-view' }` } className={ `${ showStackedView ? 'row' : '' }` }>
            <div className={ `${ showStackedView ? 'col-6 no-right-pad' : '' }` }>
              { !isGettingForm && formError == null && formType != null && <section className={ `form ${ showStackedView ? 'stacked' : '' }` }>
                <div className={ this.getWrapperClasses() }>
                  <div>
                    <div id="form-wrapper" className={ `form-wrapper ${ isGettingForm ? 'loading-form' : '' }` }>
                      <CustomerAvatar className="mobile-username" />
                      <div id="form-content" className="form-content">
                        <div className={ `${ showStackedView ? 'stacked-page' : '' }` }>
                          { (isGettingForm ? <div className="loading-container"><LoadingWrapper text="Please Wait..." /></div> : this.renderForm()) }
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                { !showStackedView && this.getActionElements() }
              </section> }
              { !showStackedView && <React.Fragment>
                <ValidationResults
                  result={ validationResult }
                  showResult={ showResult }
                  dirty={ dirty }
                  onClick={ isOpen => this.setState({
                    showResult: isOpen
                  }) }
                  onSignAndSubmit={ () => this.signAndSubmit() }
                  onUploadDocumentClick={ () => this.onUploadDocumentClick() }
                  previewOnlyMode={ previewOnlyMode }
                />
              </React.Fragment> }
            </div>
            { showStackedView && !isGettingForm && !formError && <div className="col-6 double-left-pad validation-results-stacked-column">
              <ValidationResultsStacked
                result={ validationResult }
                showResult={ showResult && showMobileView }
                dirty={ dirty }
                onSignAndSubmit={ () => this.signAndSubmit() }
                previewOnlyMode={ previewOnlyMode }
                isGettingForm={ workflowState == null }
              />
              { this.getActionElements() }
            </div> }
          </div>}
          { !showPinVerification && submissionStage === submissionStages.Confirmation && this.showSignAndSubmit() && <FormConfirmation
            onConfirm={ () => this.onFormConfirmationConfirm() }
            onCancel={ () => this.onFormConfirmationCancel() }
            intl={ intl }
            previewOnlyMode={ previewOnlyMode }
            onClickHandler={ () => this.downloadPreviewPdf() }
            isFetchingPdf={ isFetchingPdfForm }

          /> }
          { !showPinVerification && submissionStage === submissionStages.Certification && <PenaltiesOfPerjuryModal
            formType={ formType }
            onConfirm={ () => this.onCertificationConfirm() }
            onCancel={ () => this.onCertificationCancel() }
          /> }
          { submissionStage === submissionStages.Signing && <FormSigning
            formType={ formType }
            id={ formId }
            onCancel={ () => this.onFormSigningCancel() }
          /> }
          { showSupportingDocs && <SupportingDocumentsModal
            id={ formId }
            supportingDocs={ supportingDocs }
            onCancel={ () => this.setState({ showSupportingDocs: false }) }
          /> }
          { showPinVerification && this.showSignAndSubmit() && <PinVerificationModal
            onCancel={ () => this.onPinVerificationCancel() }
            onConfirm={ (pin, pinVerificationType) => this.onPinVerificationConfirm(pin, pinVerificationType) }
          /> }
        </React.Fragment> }
      </React.Fragment>
    );
  }
}

function getReduxFormDirtyStatus(state, ownProps) {
  let reduxFormName = '';
  if (ownProps != null && ownProps.match != null && ownProps.match.params != null && ownProps.match.params.formType != null) {
    let formName = ownProps.match.params.formType;
    const splitIndex = ownProps.match.params.formType.indexOf('_');
    if (splitIndex > -1) {
      formName = ownProps.match.params.formType.substring(0, splitIndex);
    }
    reduxFormName = `${ formName }-form`;
  }
  return { dirty: isDirty(reduxFormName)(state), reduxFormName };
}

function mapStateToProps(state, ownProps) {
  const {
    app: { onlineConfig: { previewOnlyMode, formDisplayMode, usePreQualifyingQuestionnaire, authenticationType, customerEmailAddress, defaultFormType } },
    authentaLink: { authenticationPinVerified, showPinVerification },
    form: {
      isValidatingForm,
      validationResult,
      supportingDocs,
      workflowState,
      isGettingForm,
      formData,
      formError,
      instructionToggled,
      isFormConfirmationShown,
      submissionStage,
      pdfData,
      isFetchingPdfForm,
    }
  } = state;

  const { dirty, reduxFormName } = getReduxFormDirtyStatus(state, ownProps);

  return {
    isValidatingForm,
    validationResult,
    supportingDocs,
    workflowState,
    isGettingForm,
    formError,
    formData,
    instructionToggled,
    dirty,
    reduxFormName,
    isFormConfirmationShown,
    submissionStage,
    pdfData,
    isFetchingPdfForm,
    previewOnlyMode,
    formDisplayMode,
    usePreQualifyingQuestionnaire,
    authenticationType,
    authenticationPinVerified,
    showPinVerification,
    emailAddress: customerEmailAddress,
    defaultFormType
  };
}

const mapDispatchToProps = dispatch => ({ dispatch });

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(FormContainer));
